/**
 * POST /v1/feedback-board/submit — a school admin submits new feedback.
 *
 * Creates a Linear issue in the SCH team's Triage state (intake), tagged with a
 * type label (Feature/Improvement/Bug), and links it to the school's Linear
 * customer as a need so it surfaces on the board's `inbox` column immediately.
 * Admin-gated, school-scoped (active x-school-id → School.publicId → Linear
 * customer externalId).
 *
 * Triage items land in the board's `inbox` column (freshly-submitted intake, not
 * yet accepted); the response carries only the created issue identifier for an
 * optimistic confirmation — not a board card.
 */

import { z } from 'zod'

/**
 * Submission type — maps to a Linear type label backend-side. Kept customer-
 * facing and small (origin: Feature / Improvement / Bug).
 */
export const SubmitFeedbackTypeSchema = z.enum(['feature', 'improvement', 'bug'])
export type SubmitFeedbackType = z.infer<typeof SubmitFeedbackTypeSchema>

export const SubmitFeedbackRequestSchema = z.object({
  /** Which kind of request this is (drives the Linear type label). */
  type: SubmitFeedbackTypeSchema,
  /** Short summary → the Linear issue title. */
  title: z.string().trim().min(1).max(200),
  /** The full request → the Linear issue description + customer-need body. */
  description: z.string().trim().min(1).max(5000)
})
export type SubmitFeedbackRequest = z.infer<typeof SubmitFeedbackRequestSchema>

export const SubmitFeedbackResponseSchema = z.object({
  submitted: z.literal(true),
  /** Human-readable identifier of the created issue (e.g. "SCH-300"). */
  issueIdentifier: z.string()
})
export type SubmitFeedbackResponse = z.infer<typeof SubmitFeedbackResponseSchema>
